/**
 * Inbound email schema for the client SDK.
 *
 * Extends the SDK-agnostic base from `../../documents/emailAgent/inboundEmail`
 * with a `Timestamp`-transforming `receivedAt`, and exposes typed refs used by
 * the webapp's bulk-bucket review list.
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {collection, doc, getFirestore} from 'firebase/firestore';

import {
  InboundEmailDocumentSchema,
  getBucketEmailsCollectionPath,
  getInboundEmailDocumentPath,
} from '../../documents/emailAgent/inboundEmail';
import {ClientTimestampSchema, createConverter} from '../sdk';

/** Client-SDK inbound-email schema; `receivedAt` becomes a `Timestamp`. */
export const InboundEmailSchema = InboundEmailDocumentSchema.extend({
  receivedAt: ClientTimestampSchema,
}).loose();

/** Inferred type for {@link InboundEmailSchema}. */
export type InboundEmail = z.infer<typeof InboundEmailSchema>;

const inboundEmailConverter = createConverter({
  schema: InboundEmailSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed ref to a bucket's inbound-emails subcollection. Apply `query`/`where`/
 * `orderBy` at the call site.
 *
 * @param args.bucketId - The bucket (course id or the bulk bucket id).
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection ref for `reports/{bucketId}/emails`.
 */
export function getBucketEmailsCollectionRef({
  bucketId,
  firestore = getFirestore(),
}: {
  bucketId: string;
  firestore?: Firestore;
}): CollectionReference<InboundEmail> {
  return collection(
    firestore,
    getBucketEmailsCollectionPath({bucketId}),
  ).withConverter(inboundEmailConverter);
}

/**
 * Typed ref to a single inbound-email document.
 *
 * @param args.bucketId - The bucket the email lives in.
 * @param args.emailId - The email document id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for `reports/{bucketId}/emails/{emailId}`.
 */
export function getInboundEmailDocRef({
  bucketId,
  emailId,
  firestore = getFirestore(),
}: {
  bucketId: string;
  emailId: string;
  firestore?: Firestore;
}): DocumentReference<InboundEmail> {
  return doc(
    firestore,
    getInboundEmailDocumentPath({bucketId, emailId}),
  ).withConverter(inboundEmailConverter);
}
